import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { fetchMyInvite, fetchInviteLeaderboard, shareInvite } from "../lib/invites";
import { fetchOrCreateProfile, getStoredName } from "../lib/api";
import { getPublicUrl } from "../lib/publicUrl";
import { sfx } from "../lib/sound";
import { track } from "../lib/analytics";
import AvatarRender from "./AvatarRender";

/**
 * Invite-Friends modal — the #1 organic-growth lever for a multiplayer
 * card game. Surfaces:
 *   • The user's 6-char invite code + share button
 *   • Tiered milestone rewards (1 / 3 / 5 / 10 / 25 friends) with progress
 *   • Global "Top Referrers" leaderboard (gamifies sharing)
 */
export default function InviteFriendsModal({ open, onClose }) {
  const [data, setData] = useState(null);
  const [board, setBoard] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [shareState, setShareState] = useState(""); // "", "sharing", "copied", "shared", "failed"
  const [tab, setTab] = useState("share"); // "share" | "leaders"

  useEffect(() => {
    if (!open) return;
    setErr(""); setShareState(""); setTab("share");
    setLoading(true);
    (async () => {
      try {
        // Make sure the profile exists before asking for the invite code.
        // This is idempotent and protects against a race where the user
        // opens the modal before the lobby has finished creating the profile,
        // or a stale localStorage user_id from a deleted account.
        try { await fetchOrCreateProfile(getStoredName()); } catch {}
        const [d, lb] = await Promise.all([
          fetchMyInvite(),
          fetchInviteLeaderboard(10).catch(() => []),
        ]);
        setData(d);
        setBoard(lb);
      } catch (e) {
        const detail = e?.response?.data?.detail || e?.message || "Couldn't load invite code";
        setErr(detail);
      } finally {
        setLoading(false);
      }
    })();
  }, [open]);

  const code = data?.code || "";
  const link = typeof window !== "undefined" && code
    ? `${getPublicUrl()}/?invite=${encodeURIComponent(code)}`
    : "";
  const redeemed = data?.invites_redeemed || 0;
  const rewardCap = data?.rewarded_redemption_cap || 10;
  const rewardChips = data?.reward_chips_inviter || 200;
  const remaining = data?.rewarded_redemptions_remaining ?? Math.max(0, rewardCap - redeemed);
  // Progress bar caps at the rewarded-redemption limit (default 10).
  const progressPct = Math.min(100, Math.round((redeemed / rewardCap) * 100));

  const doShare = async () => {
    if (!code || shareState === "sharing") return;
    setShareState("sharing");
    sfx.click();
    try {
      const method = await shareInvite(code);
      track("invite_shared", { method, code });
      if (method === "clipboard") setShareState("copied");
      else if (method === "none") setShareState("failed");
      else setShareState("shared");
    } catch {
      setShareState("failed");
    }
  };

  const copyLink = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      track("invite_link_copied", { code });
      setShareState("copied");
    } catch {
      setShareState("failed");
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[110] flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.85)" }}
          onClick={onClose}
          data-testid="invite-friends-modal"
        >
          <motion.div
            initial={{ scale: 0.92, y: 18 }} animate={{ scale: 1, y: 0 }}
            className="glass max-w-md w-full rounded-3xl relative flex flex-col"
            style={{ borderColor: "rgba(61,220,151,0.45)", maxHeight: "92vh" }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header — fixed at top of modal */}
            <div className="p-6 pb-4 flex-shrink-0">
              <div
                className="mx-auto mb-3 flex items-center justify-center rounded-full"
                style={{
                  width: 56, height: 56,
                  background: "linear-gradient(135deg, #3DDC97, #00A3FF)",
                  fontSize: 24, color: "#0B0B0B",
                  boxShadow: "0 0 28px rgba(61,220,151,0.45)",
                }}
              >🤝</div>
              <div className="text-center">
                <div className="label-eyebrow mb-1" style={{ color: "#3DDC97", letterSpacing: 2 }}>INVITE FRIENDS</div>
                <h2 className="font-display font-black text-2xl">+{rewardChips || 200} chips each</h2>
                <p className="text-white/55 text-xs mt-1">First {rewardCap || 10} friends count toward rewards.</p>
              </div>
            </div>

            {/* Tabs — fixed below header */}
            <div className="px-6 flex gap-2 flex-shrink-0" data-testid="invite-tabs">
              <button
                className={`flex-1 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-all ${tab === "share" ? "" : "opacity-60"}`}
                onClick={() => { sfx.click(); setTab("share"); }}
                data-testid="invite-tab-share"
                style={{
                  background: tab === "share" ? "#3DDC97" : "rgba(255,255,255,0.04)",
                  color: tab === "share" ? "#0B0B0B" : "#fff",
                  border: tab === "share" ? "1px solid #3DDC97" : "1px solid rgba(255,255,255,0.08)",
                }}
              >Your code</button>
              <button
                className={`flex-1 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-all ${tab === "leaders" ? "" : "opacity-60"}`}
                onClick={() => { sfx.click(); setTab("leaders"); }}
                data-testid="invite-tab-leaders"
                style={{
                  background: tab === "leaders" ? "#FFD700" : "rgba(255,255,255,0.04)",
                  color: tab === "leaders" ? "#0B0B0B" : "#fff",
                  border: tab === "leaders" ? "1px solid #FFD700" : "1px solid rgba(255,255,255,0.08)",
                }}
              >Top referrers</button>
            </div>

            {/* Body — scrollable when content overflows on mobile */}
            <div className="px-6 pt-5 pb-2 overflow-y-auto" style={{ flex: "1 1 auto", minHeight: 0 }}>
              {loading && (
                <div className="text-white/50 text-sm py-6 text-center" data-testid="invite-friends-loading">Loading…</div>
              )}
              {err && !loading && (
                <div className="text-xs mb-3 text-center" style={{ color: "#FF8A8A" }} data-testid="invite-friends-error">
                  {err}
                </div>
              )}

              {!loading && !err && tab === "share" && code && (
                <>
                  <div
                    className="font-mono font-black text-3xl tracking-[0.4em] rounded-xl px-4 py-4 mb-2 text-center"
                    style={{
                      background: "rgba(255,255,255,0.04)",
                      border: "1.5px solid rgba(61,220,151,0.35)",
                      color: "#3DDC97",
                      userSelect: "all",
                    }}
                    data-testid="invite-friends-code"
                  >{code}</div>
                  <p className="text-[11px] text-white/55 text-center mb-4">
                    Share this code (or the link below). Your friend enters it the
                    first time they open Quattro and you both get +{rewardChips} chips.
                  </p>

                  {/* Progress bar to the rewarded-redemption cap */}
                  <div className="mb-4" data-testid="invite-progress">
                    <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-white/55 mb-1.5">
                      <span>{redeemed} / {rewardCap} rewarded invites</span>
                      <span style={{ color: "#3DDC97" }}>+{rewardChips} each</span>
                    </div>
                    <div className="h-2 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.06)" }}>
                      <motion.div
                        initial={{ width: 0 }} animate={{ width: `${progressPct}%` }}
                        transition={{ duration: 0.7, ease: "easeOut" }}
                        style={{ height: "100%", background: "linear-gradient(90deg, #3DDC97, #FFD700)" }}
                      />
                    </div>
                    <div className="text-xs text-white/65 mt-1.5">
                      {remaining > 0
                        ? `${remaining} more reward${remaining === 1 ? "" : "s"} available · +${rewardChips} chips for you and your friend each.`
                        : `You've earned all ${rewardCap * rewardChips} chips from invites. You can still share — friends join with your code, just without a chip bonus.`}
                    </div>
                  </div>

                  <button
                    className="btn-primary w-full mb-2"
                    onClick={doShare}
                    disabled={shareState === "sharing"}
                    data-testid="invite-friends-share-btn"
                    style={{ background: "#3DDC97", color: "#0B0B0B" }}
                  >
                    {shareState === "sharing" ? "Opening…" :
                     shareState === "shared"  ? "Shared!" :
                     shareState === "copied"  ? "Copied to clipboard!" :
                     shareState === "failed"  ? "Try again" :
                     "Share with friends"}
                  </button>
                  <button
                    className="btn-ghost w-full text-xs mb-3"
                    onClick={copyLink}
                    data-testid="invite-friends-copy-link-btn"
                  >Copy invite link</button>

                  {/* How it works — make the flow + reward unambiguous */}
                  <div
                    className="rounded-xl p-3 mb-3 text-[11px] leading-relaxed text-white/65"
                    style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}
                    data-testid="invite-how-it-works"
                  >
                    <div className="label-eyebrow text-white/55 mb-1.5" style={{ fontSize: 9 }}>HOW IT WORKS</div>
                    <ol className="list-decimal pl-4 space-y-0.5">
                      <li>Tap <b className="text-white">Share with friends</b> — the link goes out via your phone's share sheet (or copied to clipboard on desktop).</li>
                      <li>Your friend opens the link. On first launch they tap <b className="text-white">Redeem</b> → both of you get <b style={{ color: "#3DDC97" }}>+{rewardChips} chips</b>.</li>
                      <li>The first <b className="text-white">{rewardCap} friends</b> you invite each earn you chips. After that, friends can still join with your code, just without a chip bonus — but you'll still appear on the top-referrer leaderboard.</li>
                    </ol>
                  </div>
                </>
              )}

              {!loading && !err && tab === "leaders" && (
                <div data-testid="invite-leaderboard">
                  {board.length === 0 ? (
                    <div className="text-center text-white/50 text-sm py-6">
                      Be the first referrer on the board!
                    </div>
                  ) : (
                    <div className="space-y-1.5 max-h-[42vh] overflow-y-auto">
                      {board.map((r, i) => {
                        const place = i + 1;
                        const medal = place === 1 ? "🥇" : place === 2 ? "🥈" : place === 3 ? "🥉" : `#${place}`;
                        return (
                          <div
                            key={r.user_id}
                            className="flex items-center gap-3 px-3 py-2 rounded-xl"
                            style={{
                              background: place <= 3 ? "rgba(255,215,0,0.06)" : "rgba(255,255,255,0.02)",
                              border: place <= 3 ? "1px solid rgba(255,215,0,0.25)" : "1px solid rgba(255,255,255,0.05)",
                            }}
                            data-testid={`leaderboard-row-${place}`}
                          >
                            <div className="font-display font-black text-sm w-8 text-center" style={{ color: place <= 3 ? "#FFD700" : "rgba(255,255,255,0.55)" }}>
                              {medal}
                            </div>
                            <div className="flex-shrink-0">
                              <AvatarRender avatar={r.avatar} name={r.name} size={36} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-display font-bold truncate text-sm">{r.name}</div>
                              <div className="text-[10px] text-white/45">
                                {r.invites_redeemed} friend{r.invites_redeemed === 1 ? "" : "s"}
                              </div>
                            </div>
                            <div className="font-mono font-bold text-sm" style={{ color: "#3DDC97" }}>
                              +{(Math.min(r.invites_redeemed, rewardCap) * rewardChips).toLocaleString()}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer — always visible (Close button stays in view on mobile) */}
            <div className="px-6 pt-2 pb-5 flex-shrink-0">
              <button
                className="btn-ghost text-xs w-full"
                onClick={onClose}
                data-testid="invite-friends-close-btn"
              >Close</button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
